import { ChangeEvent } from 'react';
import { FormControl, TextInput, Textarea } from '@primer/react';
import styled from 'styled-components';
import { SectionContainer, SectionTitle } from './shared/section-styles';
import { AppcuesEventId } from '@upright/ui/integrations/appcues/types';
import { trackAppcuesEvent } from '@upright/ui/integrations/appcues';

interface BasicPropertiesProps {
  id: string;
  /** A business unit rather than a product: it has children, no matching, and different wording. */
  isSegment: boolean;
  title: string;
  description: string;
  revenueShare?: number;
  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string) => void;
  onRevenueShareChange: (revenueShare: number | undefined) => void;
  /**
   * Whether to offer the revenue field. The field reads and writes `revenueShare` as a
   * *percentage* (×100, capped at 100), which is only correct where that value is a fraction of
   * revenue. The segment editor treats the same value as a free relative weight — its "Revenue
   * or %" column accepts a raw revenue figure and the resolver rescales siblings around it — so
   * there the field would misread the stored number and a "correction" inside its 0–100 range
   * would silently re-weight the whole set. Segment-editor callers hide it and keep revenue in
   * that column instead (PLT-6329). Defaults to shown, for the flows where the value really is a
   * share: the legacy flat editor, onboarding, and the chat side panel.
   */
  showRevenueShare?: boolean;
}

export function BasicProperties({
  id,
  isSegment,
  title,
  description,
  revenueShare,
  onTitleChange,
  onDescriptionChange,
  onRevenueShareChange,
  showRevenueShare = true,
}: BasicPropertiesProps) {
  return (
    <>
      <SectionContainer>
        <FormControl>
          <FormControl.Label>
            <SectionTitle>{isSegment ? 'Segment name' : 'Product title'}</SectionTitle>
          </FormControl.Label>
          <TextInputWrap>
            <TextInput
              style={{ width: '100%' }}
              value={title}
              onBlur={() => {
                trackAppcuesEvent(AppcuesEventId.productEditTitleFormInteraction, {
                  productId: id,
                  productTitle: title,
                });
              }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                onTitleChange(e.target.value);
              }}
            />
          </TextInputWrap>
        </FormControl>
      </SectionContainer>
      <SectionContainer>
        <FormControl>
          <FormControl.Label>
            <SectionTitle>{isSegment ? 'Segment description' : 'Product description'}</SectionTitle>
          </FormControl.Label>
          <TextInputWrap>
            <Textarea
              style={{ width: '100%' }}
              value={description}
              onBlur={() => {
                trackAppcuesEvent(AppcuesEventId.productEditDescriptionFormInteraction, {
                  productId: id,
                  productTitle: title,
                });
              }}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                onDescriptionChange(e.target.value);
              }}
              rows={3}
            />
          </TextInputWrap>
        </FormControl>
      </SectionContainer>
      {showRevenueShare && (
        <SectionContainer>
          <FormControl>
            <FormControl.Label>
              <SectionTitle>Revenue share (%)</SectionTitle>
            </FormControl.Label>
            <TextInputWrap>
              <TextInput
                style={{ width: '100%' }}
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={revenueShare !== undefined ? parseFloat((revenueShare * 100).toPrecision(10)) : ''}
                onBlur={() => {
                  trackAppcuesEvent(AppcuesEventId.productEditRevenueShareFormInteraction, {
                    productId: id,
                    productTitle: title,
                  });
                }}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  onRevenueShareChange(value === '' ? undefined : parseFloat(value) / 100);
                }}
                placeholder="Enter revenue share percentage"
              />
            </TextInputWrap>
          </FormControl>
        </SectionContainer>
      )}
    </>
  );
}

const TextInputWrap = styled.div`
  width: 100%;
  > span {
    width: 100% !important;
  }
`;
